import { type PropsWithChildren, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStatusMessage } from '@ev-tech/ui-core-components/StatusMessageProvider';
import { alertVariants } from '@ev-tech/ui-core-components/Alert';
import { getABTestContext, getAiSearchContext, getShopContext } from '@ev-tech/snowplow-library';
import { type SearchListing } from '@ev-tech/search-modules-api';

import { type WatchlistItem, WatchlistProvider } from './WatchlistProvider';
import { useMapVisibilityStore } from './MapVisibilityStoreProvider';
import { mapSearchStateToAiSearchContext } from '../utils/tracking';
import { useAppContext } from '../hooks/useAppContext';
import { useSearchStore } from '../hooks/stores/search/useSearchStore';
import { SearchMode } from '../hooks/stores/search/slices/searchModeSlice';

export const SearchWatchlistProvider = ({
  children,
  watchlist,
  onWatchlistClick,
}: PropsWithChildren<{ watchlist?: WatchlistItem[]; onWatchlistClick?: (property: SearchListing) => void }>) => {
  const queryClient = useQueryClient();
  const {
    options: { language },
    api,
    t,
    countryCode,
    tracking,
  } = useAppContext();

  const masterDataShopIds = useSearchStore((state) => state.masterDataShopIds);
  const masterDataLiPaIds = useSearchStore((state) => state.masterDataLiPaIds);
  const businessArea = useSearchStore((state) => state.objectTypeCategory);
  const isMapToggledOn = useMapVisibilityStore((state) => state.isMapActive);
  const shopIds = useSearchStore((state) => state.shopIds);
  const preferences = useSearchStore((state) => state.preferences);
  const searchMode = useSearchStore((state) => state.searchMode);

  const { showMessage } = useStatusMessage();

  const onCreate = useCallback(
    (status: 'success' | 'error') => {
      showMessage(t(status === 'success' ? 'search.userAccount.favorites.added' : 'search.userAccount.favorites.added.failed'), {
        alertType: status === 'success' ? alertVariants.success : alertVariants.error,
      });
    },
    [showMessage, t],
  );

  const onDelete = useCallback(
    (status: 'success' | 'error') => {
      showMessage(t(status === 'success' ? 'search.userAccount.favorites.deleted' : 'search.userAccount.favorites.deleted.failed'), {
        alertType: status === 'success' ? alertVariants.success : alertVariants.error,
      });
    },
    [showMessage, t],
  );

  const additionalTrackingContexts = useMemo(
    () => [
      getShopContext({
        shop_id: masterDataShopIds?.[0],
        license_id: masterDataLiPaIds?.[0],
        business_division_name: businessArea,
        office_id: shopIds?.[0],
      }),
      getABTestContext({
        id: 'isMapToggledOn',
        name: 'isMapToggledOn',
        variant: isMapToggledOn ? 'true' : 'false',
        variant_description: 'map initially toggled on or off',
      }),
      (property: SearchListing) =>
        getAiSearchContext(
          mapSearchStateToAiSearchContext({
            preferences,
            isAiSearchEnabled: searchMode === SearchMode.AI,
            preferenceScoreOverall: property.preferenceScoreOverall,
          }),
        ),
    ],
    [businessArea, isMapToggledOn, masterDataLiPaIds, masterDataShopIds, shopIds, preferences, searchMode],
  );

  return (
    <WatchlistProvider
      onWatchlistClick={onWatchlistClick}
      watchlist={watchlist}
      onCreate={onCreate}
      onDelete={onDelete}
      queryClient={queryClient}
      language={language}
      api={api}
      countryCode={countryCode}
      tracking={tracking}
      additionalTrackingContexts={additionalTrackingContexts}
    >
      {children}
    </WatchlistProvider>
  );
};
